/**
 * Nav
 * ----
 * Top-level orchestrator for the app chrome. Decides which navigation
 * surface to render for the current viewport:
 *
 *   - viewport >= 1280px: full 240px sidebar (`SidebarNav variant="full"`).
 *   - viewport 768-1279px: 64px collapsed rail (`SidebarNav variant="rail"`).
 *       Users can click the toggle to expand-to-full (still in-layout).
 *   - viewport <768px: sidebar hidden, `TopBar` + `MobileNavSheet` instead.
 *
 * Persistence:
 *   The user-initiated collapse preference is stored in localStorage under
 *   `cc.sidebar.collapsed.<userId>` when the user id is known, else under
 *   `cc.sidebar.collapsed` as a fallback. Namespacing per-user avoids leaking
 *   a power-user's rail preference onto a less-experienced teammate on a
 *   shared workstation (a real pattern for owner-admin desks).
 *
 * Contextual sub-navigation (e.g. "Overview / Continue Filing") has moved
 * out of this file into `ContextualPageNav`, which individual page routes
 * render in their own layouts. See the note in `ContextualPageNav.tsx`.
 */
import React, { useEffect, useState, useCallback } from "react";
import SidebarNav, {
  type SidebarVariant,
} from "@/components/common/SidebarNav";
import TopBar from "@/components/common/TopBar";
import MobileNavSheet from "@/components/common/MobileNavSheet";
import { useMeQuery } from "@/queries/queries";
import { cn } from "@/lib/utils";

// Tailwind breakpoint mirrors — keep in sync with tailwind.config.js defaults.
const MD = 768;
const XL = 1280;

const STORAGE_PREFIX = "cc.sidebar.collapsed";
const storageKey = (userId: string | number | undefined) =>
  userId != null ? `${STORAGE_PREFIX}.${userId}` : STORAGE_PREFIX;

type Viewport = "mobile" | "tablet" | "desktop";

const getViewport = (): Viewport => {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < MD) return "mobile";
  if (w < XL) return "tablet";
  return "desktop";
};

const Nav: React.FC = () => {
  const { data: user } = useMeQuery();
  const [viewport, setViewport] = useState<Viewport>(getViewport);
  const [mobileOpen, setMobileOpen] = useState(false);

  // `userCollapsed` is the explicit user preference. It only applies on
  // tablet+desktop. On mobile, the sheet drives things independently.
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null);

  // --- Load persisted preference once we know who the user is.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const key = storageKey(user?.id);
      const raw = window.localStorage.getItem(key);
      // Fallback to legacy global key so existing users don't feel a reset.
      const legacy =
        raw == null ? window.localStorage.getItem(STORAGE_PREFIX) : null;
      const v = raw ?? legacy;
      if (v === "true") setUserCollapsed(true);
      else if (v === "false") setUserCollapsed(false);
    } catch {
      // localStorage can throw in private mode / strict sandboxes — not fatal.
    }
  }, [user?.id]);

  // --- Track viewport. We use matchMedia for efficiency over a resize handler.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mdMql = window.matchMedia(`(min-width: ${MD}px)`);
    const xlMql = window.matchMedia(`(min-width: ${XL}px)`);
    const update = () => setViewport(getViewport());
    update();
    mdMql.addEventListener("change", update);
    xlMql.addEventListener("change", update);
    return () => {
      mdMql.removeEventListener("change", update);
      xlMql.removeEventListener("change", update);
    };
  }, []);

  // --- Auto-close the mobile sheet if the viewport grows past mobile.
  useEffect(() => {
    if (viewport !== "mobile" && mobileOpen) setMobileOpen(false);
  }, [viewport, mobileOpen]);

  const persistCollapsed = useCallback(
    (next: boolean) => {
      setUserCollapsed(next);
      try {
        window.localStorage.setItem(storageKey(user?.id), String(next));
      } catch {
        // ignore
      }
    },
    [user?.id],
  );

  // --- Decide the current sidebar variant for desktop/tablet rendering.
  // On tablet, we default to rail unless the user has explicitly expanded it.
  // On desktop, we default to full unless the user has explicitly collapsed it.
  const sidebarVariant: SidebarVariant =
    viewport === "tablet"
      ? userCollapsed === false
        ? "full"
        : "rail"
      : userCollapsed === true
        ? "rail"
        : "full";

  const toggleVariant = () => {
    // A click on the toggle flips the *effective* state for this viewport.
    if (viewport === "tablet") {
      // tablet default is rail → toggle to full means userCollapsed=false
      persistCollapsed(sidebarVariant === "full");
    } else {
      // desktop default is full → toggle to rail means userCollapsed=true
      persistCollapsed(sidebarVariant === "full");
    }
  };

  // Collapse the sidebar when the user picks a destination. Two reasons:
  //   1) Conventional SaaS pattern — once you've navigated, you want content
  //      width, not nav width. Notion, Linear, Retool all do this after a
  //      deliberate interaction.
  //   2) It keeps the rail's tooltip-per-icon affordance live on the next page
  //      so navigation stays discoverable without stealing viewport.
  // No-op when we're already on rail (nothing to collapse) or on mobile (the
  // sheet closes via onNavigate on the mobile branch above).
  const collapseOnNavigate = useCallback(() => {
    if (viewport === "mobile") return;
    if (sidebarVariant === "rail") return;
    persistCollapsed(true);
  }, [viewport, sidebarVariant, persistCollapsed]);

  // Widths must match `__root.tsx` padding utilities. Keep these in lockstep.
  const desktopWidth = sidebarVariant === "rail" ? "w-16" : "w-60";

  // Sync the active desktop sidebar width to a CSS variable so the
  // `<main>` reservation in __root.tsx tracks rail/full transitions.
  // Without this, collapsing to rail leaves a 176px empty gutter
  // because main has `xl:pl-60` (15rem) hardcoded against a sidebar
  // that just shrank to 4rem.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const px = sidebarVariant === "rail" ? "4rem" : "15rem";
    document.documentElement.style.setProperty("--sidebar-w-xl", px);
  }, [sidebarVariant]);

  return (
    <>
      {/* Mobile: top bar + left sheet */}
      {viewport === "mobile" && (
        <>
          <TopBar onOpenMenu={() => setMobileOpen(true)} />
          <MobileNavSheet
            open={mobileOpen}
            onOpenChange={setMobileOpen}
            title="Carbon Clear navigation"
          >
            <SidebarNav
              variant="full"
              showToggle={false}
              onNavigate={() => setMobileOpen(false)}
            />
          </MobileNavSheet>
        </>
      )}

      {/* Tablet & desktop: persistent sidebar fixed to the left. The Outlet
          padding in __root.tsx (`md:pl-16 xl:pl-60`) reserves space. */}
      {viewport !== "mobile" && (
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-20 border-r border-border bg-background",
            desktopWidth,
            "transition-[width] duration-150 ease-out",
          )}
          aria-label="Primary navigation"
        >
          <SidebarNav
            variant={sidebarVariant}
            onToggleVariant={toggleVariant}
            onNavigate={collapseOnNavigate}
          />
        </aside>
      )}
    </>
  );
};

export default Nav;
